import { ExternalLinkIcon } from '@radix-ui/react-icons'
import { useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import { type StepComponentProps } from 'components/member_onboarding_v2/types'
import { Banner, ButtonGroup, TextLink } from 'slab'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment } from 'utils/web_api'
import { type MemberOnboardingMedicalPlan } from 'utils/web_api.schemas'

import { type PlanTableType } from './Section'
import { CarrierLogo } from '../../../members/components/CarrierLogo'
import { type MedicalPlanDisplayItem } from '../../medicalPlanTypes'
import {
  calculateRemainingBudgetForTieredAllowance,
  extractInNetworkString,
  extractOutOfNetworkString,
  getRecommendationReasonLabels,
} from '../../utils'
import {
  getRemainingAllowance,
  SelectPlanButton,
} from '../MemberOnboardingPlanCard'
import { MetalTierBadge } from '../MetalTierBadge/MetalTierBadge'
import { OnboardingGenericErrorMessage } from '../OnboardingGenericErrorMessage'
import { PlanBadge } from '../PlanBadge/PlanBadge'
import { PlanCard } from '../PlanCard'
import { FeatureList, PlanTable } from '../PlanTable'
import { useProviderInNetworkVerificationContext } from '../ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'
import { ProviderInNetworkVerificationBanner } from '../ProviderInNetworkVerificationModal/ProviderInNetworkVerificationBanner'
import { useProviderInNetworkVerificationResults } from '../ProviderInNetworkVerificationResults/useProviderInNetworkVerificationResults'

// TODO rename the file to MedicalDetailPlanTable
interface DetailPlanTableProps {
  forComparison?: boolean;
  navigateToNextStep?: () => void;
  planDisplayItems: Array<MedicalPlanDisplayItem>;
  type?: PlanTableType;
  navigateToSpecificStep: StepComponentProps['navigateToSpecificStep'];
}

type PlanSectionRow = {
  key: Exclude<keyof MemberOnboardingMedicalPlan, 'hsa_eligible' | 'actuarial_value' | 'computed_actuarial_value'>;
  header: string;
}

type PlanSection = {
  title: string;
  rows: PlanSectionRow[];
}

const PLAN_SECTIONS: PlanSection[] = [
  {
    title: 'Deductibles',
    rows: [
      { key: 'individual_medical_deductible', header: 'Individual medical' },
      { key: 'individual_drug_deductible', header: 'Individual drug' },
      { key: 'family_medical_deductible', header: 'Family medical' },
      { key: 'family_drug_deductible', header: 'Family drug' },
    ],
  },
  {
    title: 'Out-of-pocket Maximums',
    rows: [
      { key: 'individual_medical_moop', header: 'Individual medical' },
      { key: 'individual_drug_moop', header: 'Individual drug' },
      { key: 'family_medical_moop', header: 'Family medical' },
      { key: 'family_drug_moop', header: 'Family drug' },
    ],
  },
  {
    title: 'Doctor visits',
    rows: [
      { key: 'preventative_care_benefit', header: 'Preventive care' },
      { key: 'primary_care_physician_benefit', header: 'Primary care' },
      { key: 'specialist_benefit', header: 'Specialist' },
    ],
  },
  {
    title: 'Medication',
    rows: [
      { key: 'generic_drugs_benefit', header: 'Generic drugs' },
      { key: 'preferred_brand_drugs_benefit', header: 'Preferred brand drugs' },
      {
        key: 'non_preferred_brand_drugs_benefit',
        header: 'Non-preferred brand drugs',
      },
      { key: 'specialty_drugs_benefit', header: 'Specialty drugs' },
    ],
  },
  {
    title: 'Hospital & emergency care',
    rows: [
      { key: 'urgent_care_benefit', header: 'Urgent care' },
      { key: 'emergency_room_benefit', header: 'Emergency room' },
      { key: 'ambulance_benefit', header: 'Ambulance' },
      {
        key: 'outpatient_ambulatory_care_center_benefit',
        header: 'Outpatient care center',
      },
      { key: 'outpatient_facility_benefit', header: 'Facility (outpatient)' },
      { key: 'outpatient_physician_benefit', header: 'Physician (outpatient)' },
      { key: 'inpatient_facility_benefit', header: 'Facility (inpatient)' },
      { key: 'inpatient_physician_benefit', header: 'Physician (inpatient)' },
      { key: 'skilled_nursing_benefit', header: 'Skilled nursing' },
      { key: 'home_health_care_benefit', header: 'Home health care' },
      { key: 'hospice_service_benefit', header: 'Hospice' },
    ],
  },
  {
    title: 'Pediatric coverage',
    rows: [
      { key: 'child_dental_benefit', header: 'Child dental' },
      { key: 'child_eye_exam_benefit', header: 'Child eye exam' },
      { key: 'child_eyewear_benefit', header: 'Child eyewear' },
    ],
  },
  {
    title: 'Testing & equipment',
    rows: [
      { key: 'diagnostic_test_benefit', header: 'Diagnostic test' },
      { key: 'lab_test_benefit', header: 'Lab test' },
      { key: 'imaging_benefit', header: 'Imaging' },
      { key: 'imaging_physician_benefit', header: 'Imaging physician' },
      {
        key: 'durable_medical_equipment_benefit',
        header: 'Durable medical equipment',
      },
    ],
  },
  {
    title: 'Mental health & substance care',
    rows: [
      {
        key: 'outpatient_mental_health_benefit',
        header: 'Mental health (outpatient)',
      },
      {
        key: 'inpatient_mental_health_benefit',
        header: 'Mental health (inpatient)',
      },
      {
        key: 'outpatient_substance_benefit',
        header: 'Substance care (outpatient)',
      },
      {
        key: 'inpatient_substance_benefit',
        header: 'Substance care (inpatient)',
      },
    ],
  },
  {
    title: 'Pregnancy & birth',
    rows: [
      { key: 'inpatient_birth_benefit', header: 'Birth' },
      { key: 'inpatient_birth_physician_benefit', header: 'Birth physician' },
      { key: 'prenatal_care_benefit', header: 'Prenatal care' },
      { key: 'postnatal_care_benefit', header: 'Postnatal care' },
    ],
  },
  {
    title: 'Rehabilitation',
    rows: [
      { key: 'rehabilitation_services_benefit', header: 'Rehabilitation' },
      { key: 'habilitation_services_benefit', header: 'Habilitation' },
    ],
  },
] as const

interface PlanHeaderProps {
  planDisplayItem: MedicalPlanDisplayItem;
  handleSelectPlan: (plan: MedicalPlanDisplayItem) => void;
}

function PlanHeader ({ planDisplayItem, handleSelectPlan }: PlanHeaderProps) {
  const { onboardingData } = useOnboarding()
  const currentPlanEnrollment =
    onboardingData?.enrollments?.find((e) => e.covers_medical) || null
  const plan = planDisplayItem.plan
  const isSelected =
    currentPlanEnrollment &&
    currentPlanEnrollment.carrier_name?.toLowerCase() ===
      plan.carrier_name.toLowerCase() &&
    currentPlanEnrollment.plan_code?.toLowerCase() === plan.id
  const premium = Number(
    ((planDisplayItem.total_premium.subunits || 0) / 100).toFixed(2)
  )
  const isMemberOnTieredAllowance = !!planDisplayItem.tiered_allowance_employee_only_premium
  let difference = null
  if (isMemberOnTieredAllowance) {
    difference = calculateRemainingBudgetForTieredAllowance({ planDisplayItem })
  } else if (onboardingData) {
    difference = getRemainingAllowance(onboardingData, premium)
  }

  return (
    <PlanTable.ColumnHeader key={planDisplayItem.plan.id}>
      <div className='flex items-start justify-between gap-4 mb-4'>
        <div className='flex items-start gap-2'>
          {planDisplayItem.plan.carrier_image_url &&
            planDisplayItem.plan.carrier_image_url.length > 0 && (
              <CarrierLogo
                aria-hidden
                src={planDisplayItem.plan.carrier_image_url}
              />
          )}
          <div className='pt-0.5 whitespace-normal'>
            <div className='text-gray-800 body-sm mb-1'>
              {planDisplayItem.plan.friendly_name}
            </div>
            <div className='body-sm-bold text-gray-800'>
              {planDisplayItem.plan.carrier_name}
            </div>
          </div>
        </div>
        <div className='shrink-0 space-y-1 text-right'>
          <div className='font-normal text-gray-500'>Monthly premium</div>
          {difference !== null && (
            <PlanCard.Premium
              amount={premium}
              remainder={difference}
              displayMode='compact'
              isTieredAllowance={isMemberOnTieredAllowance}
            />
          )}
        </div>
      </div>
      <ButtonGroup layout='horizontal' align='center'>
        <SelectPlanButton
          isCurrentPlan={currentPlanEnrollment}
          isSelected={isSelected}
          handleSelectPlan={() => handleSelectPlan(planDisplayItem)}
          buttonStyle='primary'
        />
      </ButtonGroup>
    </PlanTable.ColumnHeader>
  )
}

interface PlanOverviewProps {
  planDisplayItems: Array<MedicalPlanDisplayItem>;
  onProviderSupportLinkClick: (plan: MedicalPlanDisplayItem['plan']) => void;
  isFerryVerificationTasksEnabled?: boolean | null;
  navigateToSpecificStep: StepComponentProps['navigateToSpecificStep']
}

function PlanOverview ({
  planDisplayItems,
  onProviderSupportLinkClick,
  isFerryVerificationTasksEnabled,
  navigateToSpecificStep,
}: PlanOverviewProps) {
  const onSelectMorePlansForVerification = () => {
    navigateToSpecificStep?.(`in_network_verification:${planDisplayItems.map(p => p.plan.id).join(',')}`)
  }
  const { canKickOffInNetworkVerificationTask, selectedProviderNpis } = useProviderInNetworkVerificationContext()

  const showInNetworkVerificationBanner = isFerryVerificationTasksEnabled && canKickOffInNetworkVerificationTask && selectedProviderNpis.length
  return (
    <PlanTable.Section
      key='Overview'
      header='Overview'
      plansCount={planDisplayItems.length}
    >
      <PlanTable.Row header='Metal tier'>
        {planDisplayItems.map(({ plan }) => (
          <PlanTable.Cell key={plan.id}>
            <MetalTierBadge tier={plan.metal_tier} />
          </PlanTable.Cell>
        ))}
      </PlanTable.Row>
      <PlanTable.Row header='Plan type'>
        {planDisplayItems.map(({ plan }) => (
          <PlanTable.Cell key={plan.id}>
            <PlanBadge type={plan.plan_type} />
          </PlanTable.Cell>
        ))}
      </PlanTable.Row>
      <PlanTable.Row header='Network'>
        {planDisplayItems.map(({ plan }) => (
          <PlanTable.Cell key={plan.id}>
            <span className='font-semibold'>{plan.network_name}</span>
          </PlanTable.Cell>
        ))}
      </PlanTable.Row>
      <PlanTable.Row header='Full plan details'>
        {planDisplayItems.map(({ plan }) => (
          <PlanTable.Cell key={plan.id}>
            {plan.sbc_url && (
              <TextLink
                href={plan.sbc_url}
                rightIcon={ExternalLinkIcon}
                target='_blank'
              >
                View plan details
              </TextLink>)}
            {!plan.sbc_url && (
              <span className='text-gray-500'>Details coming soon</span>
            )}
          </PlanTable.Cell>
        ))}
      </PlanTable.Row>
      <PlanTable.Row header='Providers' borderBottom={!showInNetworkVerificationBanner}>
        {planDisplayItems.map(({ plan, provider_in_network_map: providerInNetworkMap }) => {
          const { content } = useProviderInNetworkVerificationResults({ planId: plan.id, providerInNetworkMap })
          return (
            <PlanTable.Cell key={plan.id} borderBottom={!showInNetworkVerificationBanner}>
              {
                isFerryVerificationTasksEnabled
                  ? (
                      content
                    )
                  : (
                    <TextLink
                      href={plan.provider_support_link}
                      rightIcon={ExternalLinkIcon}
                      target='_blank'
                      onClick={() => onProviderSupportLinkClick(plan)}
                    >
                      Find in-network providers
                    </TextLink>
                    )
              }
            </PlanTable.Cell>
          )
        })}
      </PlanTable.Row>
      {
        showInNetworkVerificationBanner && (
          <PlanTable.Row>
            <PlanTable.Cell colSpan={planDisplayItems.length * 2 - 1}>
              <ProviderInNetworkVerificationBanner
                planIds={planDisplayItems.map(p => p.plan.id)}
                onSelectMorePlansForVerification={onSelectMorePlansForVerification}
              />
            </PlanTable.Cell>
          </PlanTable.Row>
        )
      }
    </PlanTable.Section>
  )
}

interface PlanSectionsProps {
  plans: Array<MedicalPlanDisplayItem['plan']>;
  type?: PlanTableType;
}

function PlanSections ({ plans, type }: PlanSectionsProps) {
  return (
    <>
      {PLAN_SECTIONS.map((section) => (
        <PlanTable.Section
          key={section.title}
          header={section.title}
          plansCount={plans.length}
          type={type}
        >
          {section.rows.map((row) => (
            <PlanTable.Row key={row.key} header={row.header}>
              {plans.map((plan) => (
                <PlanTable.Cell key={plan.id}>
                  <FeatureList>
                    <FeatureList.Item
                      coverage={extractInNetworkString(plan[row.key])}
                      label='In-network'
                    />
                    <FeatureList.Item
                      coverage={extractOutOfNetworkString(plan[row.key])}
                      label='Out-of-network'
                    />
                  </FeatureList>
                </PlanTable.Cell>
              ))}
            </PlanTable.Row>
          ))}
        </PlanTable.Section>
      ))}
    </>
  )
}

export function MedicalDetailPlanTable ({
  forComparison,
  navigateToNextStep,
  planDisplayItems,
  type = 'neutral',
  navigateToSpecificStep
}: DetailPlanTableProps) {
  const { onboardingData, enrollmentEventId, planChangeSummary, isFerryVerificationTasksEnabled } = useOnboarding()
  const [submitError, setSubmitError] = useState<GrapeAPIErrorType | null>(
    null
  )
  const plans = planDisplayItems.map((planDisplayItem) => planDisplayItem.plan)

  const onProviderSupportLinkClick = (plan: MedicalPlanDisplayItem['plan']) => {
    if (!forComparison) {
      return
    }

    const eventName = 'Support article link'
    const properties = {
      action: 'clicked',
      location: 'plan_comparison_table',
      carrier_name: plan.carrier_name,
      providerSearchEnabled: true,
      href: plan.provider_support_link,
    }

    if (window.segment) {
      window.segment.track(eventName, properties)
    }
  }

  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })

  const handleSelectPlan = (plan: MedicalPlanDisplayItem) => {
    if (!forComparison) {
      return
    }

    createEnrollment.mutate({
      enrollmentEventId,
      data: {
        covers_medical: true,
        covers_dental: false,
        covers_vision: false,
        plan_code: plan.plan.id,
        carrier_name: plan.plan.carrier_name,
        quoted_premium: (plan.total_premium?.subunits || 0) / 100,
        employee_only_premium:
          (plan.tiered_allowance_employee_only_premium?.subunits || 0) / 100,
      },
    })
  }

  let headerCell = null
  let showBadgeRow = false
  const renewalPlanId = planChangeSummary?.next_plan_year.medical_plan?.plan_code || null

  if (forComparison && (onboardingData?.recommended_medical_plans || !!renewalPlanId)) {
    const recommendedPlans = onboardingData?.recommended_medical_plans
    const recommendedPlanIds = recommendedPlans?.map((plan) => plan.plan_id)

    headerCell = plans.map((plan) => {
      if (recommendedPlanIds?.includes(plan.id) || plan.id === renewalPlanId) {
        showBadgeRow = true

        const recommendedPlan = recommendedPlans?.find(
          (p) => p.plan_id === plan.id
        )
        const renewalPlan = plans.find((p) => p.id === renewalPlanId)

        if (!recommendedPlan && !renewalPlan) {
          // should not happen but render empty cell to handle any edge cases
          return <td data-testid='empty-cell' key={plan.id} />
        }

        const planCategory = recommendedPlan?.category || 'renewal'
        const { tableLabel, tooltipLabel } =
          getRecommendationReasonLabels(planCategory)

        return (
          <PlanTable.BadgeCell key={plan.id} info={tooltipLabel}>
            {tableLabel}
          </PlanTable.BadgeCell>
        )
      } else {
        return <td data-testid='empty-cell' key={plan.id} />
      }
    })
  }

  return (
    <>
      {forComparison && submitError && (
        <div className='mb-4'>
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            <OnboardingGenericErrorMessage error={submitError} />
          </Banner>
        </div>
      )}
      <PlanTable layout='fixed'>
        {forComparison && (
          <>
            <PlanTable.Head>
              {showBadgeRow && (
                <PlanTable.BadgeRow>{headerCell}</PlanTable.BadgeRow>
              )}
              <PlanTable.HeadRow>
                {planDisplayItems.map((planDisplayItem) => (
                  <PlanHeader
                    key={planDisplayItem.plan.id}
                    planDisplayItem={planDisplayItem}
                    handleSelectPlan={handleSelectPlan}
                  />
                ))}
              </PlanTable.HeadRow>
            </PlanTable.Head>
            <PlanOverview
              planDisplayItems={planDisplayItems}
              onProviderSupportLinkClick={onProviderSupportLinkClick}
              isFerryVerificationTasksEnabled={isFerryVerificationTasksEnabled}
              navigateToSpecificStep={navigateToSpecificStep}
            />
          </>
        )}
        <PlanSections plans={plans} type={type} />
      </PlanTable>
    </>
  )
}
